import { VuexResponse } from '@helpers/vuex-action-builder'
import LocationRestriction from '@/models/locationRestriction/locationRestriction'
import { add23Hours59MinutesToDate } from '@helpers/date-helpers'
import dayjs from 'dayjs'
import { DurationUnits } from '@/constants/date/DurationUnits'
import AdminDivision from '@/constants/admin/DivisionNameIdMap'

const getDefaultState = () => {
  return {
    // Place any new state properties here
    loadingCount: 0,
    crudLoadingCount: 0,
    /** @type {Array<LocationRestriction>} */
    restrictions: [],
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    isLoading: (state) => state.loadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    restrictions: (state) => state.restrictions,
    restrictionsByAdminIds: (state) => (ids) =>
      state.restrictions.filter((a) => ids.includes(a.ownerId.toString())),
    restrictionsByLocationId: (state) => (id) =>
      state.restrictions.filter((a) => a.locationID === id),
    /**
     * Returns a list of viewable admin ids for location restrictions.
     * IT Division can see all users otherwise it shows only direct reports.
     * @return {Array<String>}
     */
    getViewableAdminIds: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['auth/currentUser']
      if (user.division === AdminDivision.IT_MELBOURNE) {
        return rootGetters['staff/getStaff'].map((s) => s.id.toString())
      }
      return rootGetters['staff/getStaff'].reduce(
        (staffList, s) => {
          if (s.reportsTo === user.id) staffList.push(s.id.toString())
          return staffList
        },
        [user.id.toString()]
      )
    },
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
    UPDATE_RESTRICTIONS(state, data) {
      state.restrictions = data.map((a) => new LocationRestriction(a))
    },
    ADD_NEW_RESTRICTIONS(state, data) {
      state.restrictions.push(...data)
    },
    REMOVE_RESTRICTIONS(state, data) {
      state.restrictions = state.restrictions.filter(
        (r) => !data.map((r2) => r2.id).includes(r.id)
      )
    },
  },
  actions: {
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },

    async loadAllActiveAndUpcomingRestrictions({ commit }) {
      return await new VuexResponse(commit)
        .request(() => this.$api.locationAlert.loadAllUpcomingAlerts())
        .withLoading()
        .withCommit('UPDATE_RESTRICTIONS')
        .go()
    },

    async loadRestrictionsByOwnerId({ commit }, ownerId) {
      return await new VuexResponse(commit)
        .request(() => this.$api.locationAlert.loadLocationAlerts(ownerId))
        .withLoading()
        .withCommit('UPDATE_RESTRICTIONS')
        .withFailureToast(this.$i18n.t('locationRestrictions.alerts.failFetch'))
        .go()
    },

    async createRestrictionByLocationIds({ commit }, requestModel) {
      // We change to the format required by backend and also add 23 hours to until to finish right at end of day
      const formattedDateModel = {
        ...requestModel,
        from: dayjs(requestModel.from).format('YYYY-MM-DDTHH:mm:ss'),
        until: add23Hours59MinutesToDate(requestModel.until).format(
          'YYYY-MM-DDTHH:mm:ss'
        ),
      }

      return await new VuexResponse(commit)
        .request(() =>
          this.$api.locationAlert.createByLocationIds(formattedDateModel)
        )
        .withLoading()
        .withSuccessToast(
          this.$i18n.t('locationRestrictions.alerts.successCreate')
        )
        .withFailureToast(
          this.$i18n.t('locationRestrictions.alerts.failCreate')
        )
        .withCommit(`ADD_NEW_RESTRICTIONS`)
        .go()
    },

    async extendRestriction({ dispatch }, { restriction, newUntilDate }) {
      // We don't modify existing alert, rather we create a new one starting when the previous one finishes
      const requestModel = {
        ids: [restriction.locationID],
        restriction: restriction.restriction,
        message: restriction.hasDefaultMessage
          ? LocationRestriction.createDefaultMessage(newUntilDate)
          : restriction.message,
        from: restriction.dateUntil.add(1, DurationUnits.MINUTE),
        until: newUntilDate,
      }
      return await dispatch('createRestrictionByLocationIds', requestModel)
    },

    async removeRestriction({ commit }, restrictionId) {
      return await new VuexResponse(commit)
        .request(() =>
          this.$api.locationAlert.removeLocationAlertsByIds({
            alertIds: [restrictionId],
          })
        )
        .withLoading()
        .withSuccessToast(
          this.$i18n.t('locationRestrictions.alerts.successRemove')
        )
        .withFailureToast(
          this.$i18n.t('locationRestrictions.alerts.failRemove')
        )
        .withCommit(`REMOVE_RESTRICTIONS`)
        .go()
    },
  },
}
